import React from 'react';
import { Modal, RadioButton, TextArea } from '@gerdau/hefesto';
import { Chips } from '@gerdau/hefesto-mais-web';

import { UserService } from '../../../services/User';
import { useDispatch } from 'react-redux';
import { saveUser } from '../../../store/ducks/user/actions';
import {
  EResearchNps,
  ISaveUser,
  IUserState
} from '../../../store/ducks/user/types';
import { addToast } from '../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../store/ducks/toast/types';

import {
  ChipsContainer,
  ChipsDescription,
  ChipsWrapper,
  Container,
  ModalContainer,
  ModalSubtitle,
  SurveyContainer,
  SurveySuccess,
  SurveyTitle,
  SurveyHeader,
  RadioButtonWrapper,
  SurveyRadioContainer
} from './styles';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import useDebounce from '../../../hooks/useDebounce';
import { getTaggingInformationScreen } from '../../../utils/Tagging';

interface IEGerdauSurveyProps {
  onCloseModal: () => void;
  user: IUserState;
}

const radioQuestion = [
  {
    value: 'Muito desapontado',
    label: 'Muito desapontado'
  },
  {
    value: 'Um pouco desapontado',
    label: 'Um pouco desapontado'
  },
  {
    value: 'Não ficaria desapontado',
    label: 'Não ficaria desapontado'
  },
  {
    value: 'Não faria diferença',
    label: 'Não faria diferença'
  }
];

const EGerdauSurvey: React.FC<IEGerdauSurveyProps> = ({
  onCloseModal,
  user
}) => {
  const dispatch = useDispatch();

  const [rating, setRating] = React.useState<string[]>();
  const [radioBtn, setRadioBtn] = React.useState<string>();
  const [npsText, setNpsText] = React.useState<string | undefined>(undefined);
  const [surveySent, setSurveySent] = React.useState(false);

  const { screenName } = getTaggingInformationScreen(window.location.pathname);

  const [loading, setLoading] = React.useState(false);

  const disabled = loading || !rating || rating.length <= 0 || !radioBtn;

  const plainOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(
    (opt) => ({
      id: opt,
      label: opt
    })
  );

  React.useEffect(() => {
    dispatch(
      sendTagging({
        screenName: screenName,
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        event: 'feedback_modal_aberto'
      })
    );
  }, []);

  const updateUser = (improvementResearchNps: EResearchNps) => {
    const saveUserProps: ISaveUser = {
      user,
      improvementResearchNps
    };
    dispatch(saveUser(saveUserProps));
  };

  const onAnswerLaterClick = () => {
    dispatch(
      sendTagging({
        screenName: screenName,
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        event: 'feedback_avaliar_mais_tarde'
      })
    );
    updateUser(EResearchNps.PENDING);
    onCloseModal();
  };

  const onDontWantAnswerClick = () => {
    updateUser(EResearchNps.DECLINED);
    onCloseModal();
  };

  const onSendClick = () => {
    if (!rating || rating.length <= 0 || !radioBtn) {
      return;
    }
    setLoading(true);

    dispatch(
      sendTagging({
        screenName: screenName,
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        event: 'feedback_enviar'
      })
    );

    if (npsText) {
      dispatch(
        sendTagging({
          screenName: screenName,
          modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
          event: 'feedback_motivo_nota',
          motivo: npsText
        })
      );
    }

    UserService.sendNPS(rating[0], radioBtn, npsText)
      .then(() => {
        setLoading(false);
        setSurveySent(true);
        dispatch(
          sendTagging({
            screenName: screenName,
            modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
            event: 'feedback_modal_enviado'
          })
        );
      })
      .catch((err) => {
        console.error({ err });
        setLoading(false);
        dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Falha ao enviar a resposta. Por favor, tente novamente.',
            durationInMS: 5000
          })
        );
      });
  };

  const handleRating = (e: string[]) => {
    dispatch(
      sendTagging({
        screenName: screenName,
        modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
        nota: e[0],
        event: 'feedback_nota'
      })
    );
    setRating(e);
  };

  const handleQst = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputValue: string
  ) => {
    if (inputValue !== radioBtn) {
      dispatch(
        sendTagging({
          screenName: screenName,
          modal_name: 'pesquisa_de_satisfacao_gerdau_mais',
          nota: e.target.value,
          event: 'feedback_desapontado'
        })
      );

      setRadioBtn(e.target.value);
    }
  };

  const debouncedChange = useDebounce(handleQst, 300);

  const handleQstDebounced = (
    e: React.ChangeEvent<HTMLInputElement>,
    inputValue: string
  ) => debouncedChange(e, inputValue);

  return (
    <Container>
      {!surveySent && (
        <Modal
          title="Pesquisa de satisfação da Gerdau mais"
          buttons={{
            left: {
              name: 'Avaliar mais tarde',
              children: 'Avaliar mais tarde',
              onClick: () => onAnswerLaterClick(),
              loading
            },
            right: {
              name: 'Avaliar experiência',
              children: 'Avaliar experiência',
              onClick: () => onSendClick(),
              loading,
              disabled
            }
          }}
          onClose={() => onDontWantAnswerClick()}
          modalSize="md"
        >
          <ModalContainer>
            <ModalSubtitle>* Campos obrigatórios</ModalSubtitle>
            <SurveyContainer>
              <SurveyHeader>
                <SurveyTitle>
                  Qual a probabilidade de recomendar a Gerdau mais?*
                </SurveyTitle>
              </SurveyHeader>

              <ChipsContainer>
                <ChipsWrapper>
                  <Chips
                    chips={plainOptions}
                    handleSelect={handleRating}
                    type="choice"
                    disabled={loading}
                    selectedChipIds={rating}
                  />
                </ChipsWrapper>

                <ChipsDescription>
                  <span>Nada provável</span>
                  <span>Extremamente provável</span>
                </ChipsDescription>
              </ChipsContainer>
            </SurveyContainer>

            <SurveyContainer>
              <SurveyHeader>
                <SurveyTitle>
                  Se a Gerdau mais deixasse de existir, quão desapontado você
                  ficaria?*
                </SurveyTitle>
              </SurveyHeader>

              <SurveyRadioContainer>
                {radioQuestion.map((radioQst) => (
                  <RadioButtonWrapper>
                    <RadioButton
                      name={radioQst.value}
                      id={radioQst.value}
                      value={radioQst.value}
                      onChange={(e) => handleQstDebounced(e, radioQst.value)}
                      disabled={loading}
                      label={radioQst.label}
                      checked={radioQst.value === radioBtn}
                    />
                  </RadioButtonWrapper>
                ))}
              </SurveyRadioContainer>

              <TextArea
                label="Explique o motivo da sua nota"
                name="nps-textarea"
                about="nps-textarea-comment"
                resizable={false}
                supportMessage="Limite de 500 caracteres."
                placeholder="Este é um campo opcional, mas se possível, explique o que motivou suas respostas."
                maxLength={500}
                cols={70}
                value={npsText}
                disabled={loading}
                onChange={(e) => setNpsText(e.target.value)}
                style={{ width: '100%' }}
              />
            </SurveyContainer>
          </ModalContainer>
        </Modal>
      )}

      {surveySent && (
        <Modal
          title="Pesquisa de satisfação da Gerdau mais"
          buttons={{
            right: {
              name: 'Fechar',
              children: 'Fechar',
              onClick: () => onCloseModal()
            }
          }}
          onClose={() => onCloseModal()}
          modalSize="sm"
        >
          <SurveySuccess>
            <span>Suas respostas foram enviadas.</span>
            <span>Agradecemos pela participação!</span>
          </SurveySuccess>
        </Modal>
      )}
    </Container>
  );
};

export default EGerdauSurvey;
